import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { TokenGuard } from './guards/token.guard'
import { LayoutWindow } from './layouts'
import { TokenResolver } from './resolver/token.resolver'

const routes: Routes = [
	{ path: 'privacy', loadComponent: () => import('./pages/privacy/privacy.component').then((m) => m.PagePrivacy) },

	{
		path: 'unauthenticated',
		loadComponent: () =>
			import('./pages/missing-credentials/missing-credentials.component').then(
				(m) => m.MissingCredentialsComponent,
			),
	},

	{
		path: '',
		component: LayoutWindow,
		canActivate: [TokenGuard],
		resolve: {
			token: TokenResolver,
		},
		children: [
			{
				path: '',
				loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.PageDashboard),
			},
			{
				path: 'bundles',
				children: [
					{
						loadComponent: () =>
							import('./bundles/bundles/bundles.component').then((m) => m.BundlesComponent),
						path: '',
					},
					{
						loadComponent: () =>
							import('./bundles/new-bundle/new-bundle.component').then((m) => m.NewBundleComponent),
						path: 'new',
					},
					{
						loadComponent: () =>
							import('./bundles/edit-bundle/edit-bundle.component').then((m) => m.EditBundleComponent),
						path: 'edit/:id',
					},
				],
			},
			{
				path: 'zones',
				children: [
					{
						loadComponent: () => import('./zones/zones/zones.component').then((m) => m.ZonesComponent),
						path: '',
					},
					{
						loadComponent: () =>
							import('./zones/new-zone/new-zone.component').then((m) => m.NewZoneComponent),
						path: 'new',
					},
					{
						loadComponent: () =>
							import('./zones/edit-zone/edit-zone.component').then((m) => m.EditZoneComponent),
						path: 'edit/:id',
					},
				],
			},
			{
				path: 'merchandise',
				children: [
					{
						loadComponent: () =>
							import('./merchandise/campaigns/campaigns.component').then((m) => m.CampaignsComponent),
						path: '',
					},
					{
						loadComponent: () =>
							import('./merchandise/campaigns/campaigns.component').then((m) => m.CampaignsComponent),
						path: 'campaigns',
					},
					{
						loadComponent: () =>
							import('./merchandise/new-campaign/new-campaign.component').then(
								(m) => m.NewCampaignComponent,
							),
						path: 'campaigns/edit/:id',
					},
					{
						loadComponent: () =>
							import('./merchandise/new-campaign/new-campaign.component').then(
								(m) => m.NewCampaignComponent,
							),
						path: 'campaigns/new/:collectionId',
					},
					{
						loadComponent: () =>
							import('./merchandise/campaign/campaign.component').then((m) => m.CampaignComponent),
						path: 'campaigns/id/:id',
					},
				],
			},
			{
				path: 'search',
				children: [
					{
						path: '',
						loadComponent: () =>
							import('./search/search-layout/search-layout.component').then(
								(m) => m.SearchLayoutComponent,
							),
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./search/components/incoming/incoming.component').then(
										(m) => m.IncomingComponent,
									),
							},
							{
								path: 'preview',
								loadComponent: () =>
									import('./search/components/search-preview/search-preview.component').then(
										(m) => m.SearchPreviewComponent,
									),
							},
							{
								path: 'incoming-terms',
								loadComponent: () =>
									import('./search/components/incoming/incoming.component').then(
										(m) => m.IncomingComponent,
									),
							},
							{
								path: 'redirects',
								loadComponent: () =>
									import('./search/components/redirects/redirects.component').then(
										(m) => m.RedirectsComponent,
									),
							},
							{
								path: 'modifiers',
								loadComponent: () =>
									import('./search/components/modifiers/modifiers.component').then(
										(m) => m.ModifiersComponent,
									),
							},
							{
								path: 'synonyms',
								loadComponent: () =>
									import('./search/components/synonyms/synonyms.component').then(
										(m) => m.SynonymsComponent,
									),
							},
							{
								path: 'suggest',
								loadComponent: () =>
									import('./search/components/suggest/suggest.component').then(
										(m) => m.SuggestComponent,
									),
							},
							{
								path: 'saved',
								loadComponent: () =>
									import('./search/components/saved/saved.component').then((m) => m.SavedComponent),
							},
							{
								path: 'semantic',
								loadComponent: () =>
									import('./search/components/semantic/semantic.component').then(
										(m) => m.SemanticComponent,
									),
							},
						],
					},
				],
			},
			{
				path: 'collections',
				children: [
					{
						path: '',
						loadComponent: () =>
							import('./collection/collection-layout/collection-layout.component').then(
								(m) => m.CollectionLayoutComponent,
							),
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./collection/collections/collections.component').then(
										(m) => m.CollectionsComponent,
									),
							},
							{
								path: 'id/:id',
								loadComponent: () =>
									import('./collection/collection/collection.component').then(
										(m) => m.CollectionComponent,
									),
							},
							{
								path: 'id/:id/meta',
								loadComponent: () =>
									import('./collection/collection/collection.component').then(
										(m) => m.CollectionComponent,
									),
							},
							{
								path: 'id/:id/config',
								loadComponent: () =>
									import('./collection/collection/collection.component').then(
										(m) => m.CollectionComponent,
									),
							},
						],
					},
				],
			},
			{
				path: 'products',
				children: [
					{
						path: '',
						loadComponent: () =>
							import('./product/product-layout/product-layout.component').then(
								(m) => m.ProductLayoutComponent,
							),
						children: [
							{
								path: '',
								loadComponent: () =>
									import('./product/products/products.component').then((m) => m.ProductsComponent),
							},
							{
								path: 'id/:id',
								loadComponent: () =>
									import('./product/product/product.component').then((m) => m.ProductComponent),
							},
							{
								path: 'sort',
								loadComponent: () =>
									import('./product/sort-products/sort-products.component').then(
										(m) => m.SortProductsComponent,
									),
							},
							{
								path: 'badges',
								loadComponent: () =>
									import('./product/badges/badges.component').then((m) => m.BadgesComponent),
							},
							{
								path: 'badges/:id',
								loadComponent: () =>
									import('./product/badge/badge.component').then((m) => m.BadgeComponent),
							},
						],
					},
				],
			},
			{
				path: 'analytics',
				loadComponent: () =>
					import('./analytics/dashboard/dashboard.component').then((m) => m.DashboardComponent),
			},
			{
				path: 'cart',
				loadComponent: () =>
					import('./cart/cart-layout/cart-layout.component').then((m) => m.CartLayoutComponent),
				children: [
					{
						path: '',
						loadComponent: () => import('./cart/preview/preview.component').then((m) => m.PreviewComponent),
					},
					{
						path: 'gwp',
						loadComponent: () => import('./cart/gwp/gwp.component').then((m) => m.GwpComponent),
					},
				],
			},
			{
				path: 'settings',
				loadComponent: () =>
					import('./settings/settings-layout/settings-layout.component').then(
						(m) => m.SettingsLayoutComponent,
					),
				children: [
					{
						path: '',
						loadComponent: () => import('./pages/billing/billing.component').then((m) => m.PageBilling),
					},
					{
						path: 'global',
						loadComponent: () =>
							import('./settings/global/global.component').then((m) => m.GlobalComponent),
						children: [
							{
								path: 'billing',
								loadComponent: () =>
									import('./pages/billing/billing.component').then((m) => m.PageBilling),
							},
							{
								path: 'store',
								loadComponent: () =>
									import('./settings/store/store.component').then((m) => m.StoreComponent),
							},
							{
								path: 'colours',
								loadComponent: () =>
									import('./settings/colours-and-images/colours-and-images.component').then(
										(m) => m.ColoursAndImagesComponent,
									),
							},
							{
								path: 'tag-alias',
								loadComponent: () =>
									import('./settings/tag-alias/tag-alias.component').then((m) => m.TagAliasComponent),
							},
							{
								path: 'grid',
								loadComponent: () =>
									import('./settings/grid/grid.component').then((m) => m.GridComponent),
							},
							{
								path: 'grid-advanced',
								loadComponent: () =>
									import('./settings/grid-advanced/grid-advanced.component').then(
										(m) => m.GridAdvancedComponent,
									),
							},
							{
								path: 'sizes',
								loadComponent: () =>
									import('./settings/sizes/sizes.component').then((m) => m.SizesComponent),
							},
							{
								path: 'facets',
								loadComponent: () =>
									import('./settings/facets/facets.component').then((m) => m.FacetsComponent),
							},
							{
								path: 'alias',
								loadComponent: () =>
									import('./settings/alias/alias.component').then((m) => m.PageAlias),
							},
						],
					},
				],
			},
			{
				path: '**',
				loadComponent: () => import('./pages/dashboard/dashboard.component').then((m) => m.PageDashboard),
			},
		],
	},
]

@NgModule({
	imports: [RouterModule.forRoot(routes, {})],
	exports: [RouterModule],
})
export class AppRoutingModule {}
